import ApiClient from '../apiClient';
import { CPLOffer, CPLOrder, TrafficSource, PaginatedResponse, PaginationParams } from './types';

// ============ Request Params ============

export interface GetCPLOffersParams extends PaginationParams {
  id?: string;
  status?: 'active' | 'hidden' | 'moderation' | 'deactivated';
  source?: TrafficSource;
  search?: string;
  sort?: string;
}

export interface GetCPLOrdersParams extends PaginationParams {
  id?: string;
  status?: 'waiting' | 'in_progress' | 'completed' | 'canceled' | 'deactivated';
  source?: TrafficSource;
  search?: string;
  sort?: string;
  subs_from?: number;
  revenue_from?: number;
  model?: string;
  type?: 'creator' | 'agency';
}

export interface GetCreatorCPLOrdersParams extends PaginationParams {
  creator_id: string;
  id?: string;
  status?: 'waiting' | 'in_progress' | 'completed' | 'canceled' | 'deactivated';
  source?: TrafficSource;
  sort_by?: string;
  subs_form?: number;
  income?: number;
}

export interface GetPartnerCPLOffersParams extends PaginationParams {
  partner_id: string;
  id?: string;
  status?: 'active' | 'hidden' | 'moderation' | 'deactivated';
  source?: TrafficSource;
  lvl?: 'new' | 'base' | 'pro' | 'expert';
}

// ============ Response Types ============

export type CPLOffersResponse = PaginatedResponse<CPLOffer>;
export type CPLOrdersResponse = PaginatedResponse<CPLOrder>;

// ============ CPL API ============

export const cplApi = {
  // ============ CPL Offers ============

  /**
   * Получить список CPL офферов
   */
  getCPLOffers: async (params?: GetCPLOffersParams): Promise<CPLOffersResponse> => {
    const response = await ApiClient.get('/staff/offers', { params });
    return response.data;
  },

  /**
   * Обновить CPL оффер
   */
  updateCPLOffer: async (offerId: string, data: Partial<CPLOffer>): Promise<CPLOffer> => {
    const response = await ApiClient.patch(`/staff/offers/${offerId}`, data);
    return response.data;
  },

  /**
   * Одобрить CPL оффер (изменить статус на active)
   */
  approveCPLOffer: async (offerId: string): Promise<CPLOffer> => {
    const response = await ApiClient.patch(`/staff/offers/${offerId}`, { status: 'active' });
    return response.data;
  },

  /**
   * Отклонить CPL оффер (изменить статус на deactivated)
   */
  rejectCPLOffer: async (offerId: string): Promise<CPLOffer> => {
    const response = await ApiClient.patch(`/staff/offers/${offerId}`, { status: 'deactivated' });
    return response.data;
  },

  // ============ CPL Orders ============

  /**
   * Получить список CPL заказов
   */
  getCPLOrders: async (params?: GetCPLOrdersParams): Promise<CPLOrdersResponse> => {
    const response = await ApiClient.get('/staff/cpl/orders', { params });
    return response.data;
  },

  /**
   * Деактивировать CPL заказ
   */
  deactivateCPLOrder: async (orderId: string): Promise<CPLOrder> => {
    const response = await ApiClient.patch(`/staff/cpl/orders/${orderId}/deactivate`);
    return response.data;
  },

  /**
   * Получить список CPL заказов криейтора
   */
  getCreatorCPLOrders: async (creatorId: string, params?: Omit<GetCreatorCPLOrdersParams, 'creator_id'>): Promise<CPLOrdersResponse> => {
    const response = await ApiClient.get(`/staff/creators/${creatorId}/cpl/orders`, { params });
    return response.data;
  },

  /**
   * Получить список CPL офферов партнера
   */
  getPartnerCPLOffers: async (partnerId: string, params?: Omit<GetPartnerCPLOffersParams, 'partner_id'>): Promise<CPLOffersResponse> => {
    const response = await ApiClient.get(`/staff/partners/${partnerId}/cpl/offers`, { params });
    return response.data;
  },
};

export default cplApi;
